import { useEffect, useRef } from 'react'
import type { WidgetConfiguration } from '../../types'
import { useEmbedCode } from '../../hooks/useEmbedCode'

interface PreviewIframeProps {
  config: WidgetConfiguration
  tenantSlug: string
}

export function PreviewIframe({ config, tenantSlug }: PreviewIframeProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const { widgetScriptUrl } = useEmbedCode(config, tenantSlug)

  useEffect(() => {
    if (!iframeRef.current) return

    // Debounce: Wait 500ms before applying settings changes
    const timeoutId = setTimeout(() => {
      if (!iframeRef.current) return

      const generatePreviewHTML = (): string => {
        // Use environment variable for API host (configured per environment)
        // Development: http://localhost:8000 or http://api.localhost:8000
        // Production: https://api.devfctrz.com
        // Works with any custom host configuration
        // TODO: Refactor VITE_API_URL to be base URL without /api suffix globally
        const apiHost = (import.meta.env.VITE_API_URL || 'http://localhost:8000').replace(/\/api$/, '')
        
        // Add api_host to config for proxy endpoint
        const configWithApiHost = {
          ...config,
          api_host: apiHost
        }
        const settingsJson = JSON.stringify(configWithApiHost, null, 2)

        return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Widget Preview</title>
    <style>
      * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
      }
      
      body {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        background: #FCFCFC;
        padding: 20px;
        min-height: 100vh;
        display: flex;
        align-items: center;
        justify-center;
      }
      
      .preview-content {
        background: white;
        padding: 40px;
        border-radius: 12px;
        box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        max-width: 600px;
        width: 100%;
        margin: 0 auto;
      }
      
      .preview-content h1 {
        color: #1a202c;
        margin-bottom: 16px;
        font-size: 28px;
      }
      
      .preview-content p {
        color: #4a5568;
        line-height: 1.6;
        margin-bottom: 12px;
      }
      
      .highlight {
        background: #edf2f7;
        padding: 16px;
        border-radius: 8px;
        margin-top: 20px;
        border-left: 4px solid #667eea;
      }
      
      .highlight p {
        margin-bottom: 0;
        font-size: 14px;
      }
    </style>
  </head>
  <body>
    <div class="preview-content">
      <h1>🎨 Widget Preview</h1>
      <p>
        This is a simulated website page to demonstrate how the chat widget will appear on your actual website.
      </p>
      <p>
        The widget button should appear in the bottom-right corner. Click it to start a conversation and test your customizations!
      </p>
      <div class="highlight">
        <p>
          <strong>💡 Tip:</strong> Try customizing the widget colors and settings in the panel to the right, and watch the changes update in real-time.
        </p>
      </div>
    </div>
    
    <script>
      // Set widget configuration
      window.yodelChatSettings = ${settingsJson};
      
      // Load widget script
      (function() {
        var script = document.createElement('script');
        script.src = '${widgetScriptUrl}';
        script.async = true;
        document.head.appendChild(script);
      })();
    </script>
  </body>
</html>`
      }

      // Write HTML to iframe
      const iframeDoc = iframeRef.current.contentDocument
      if (iframeDoc) {
        iframeDoc.open()
        iframeDoc.write(generatePreviewHTML())
        iframeDoc.close()
      }
    }, 500) // 500ms debounce delay

    // Cleanup: Clear timeout if component unmounts or config changes
    return () => clearTimeout(timeoutId)
  }, [config, widgetScriptUrl])

  return (
    <iframe
      ref={iframeRef}
      className="w-full h-full border-0 rounded-lg"
      title="Widget Preview"
      sandbox="allow-scripts allow-same-origin allow-forms"
    />
  )
}
